import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import MapGL, { AttributionControl, NavigationControl } from 'react-map-gl';
import MapWrapperContext from './MapWrapperContext';
import PinMarker from './PinMarker';
import StopsLayer from './StopsLayer';
import CurrentLocationMarker from './CurrentLocationMarker';
import ShapeOfTypes from '../../../utils/ShapeOfTypes';
import { clusterLayer, unclusteredPointLayer } from './MapClusterConfig';
import mapboxConfig, { mapData } from './MapConfig';

import StopCluster from '../../../assets/png/stop-cluster.png';
import StopBus from '../../../assets/png/stop-bus.png';
import StopFerry from '../../../assets/png/stop-ferry.png';
import StopTrain from '../../../assets/png/stop-train.png';

const {
  initialViewportState, maxZoomLevel, minZoomLevel, panAnimationDuration,
} = mapboxConfig;
const interactiveLayers = [clusterLayer.id, unclusteredPointLayer.id];

const geo = navigator.geolocation;

class StopMap extends Component {
  mapRef = createRef(null);

  constructor(props) {
    super(props);
    this.state = {
      currentLocation: null,
      selectedStop: null,
    };
  }

  componentDidMount() {
    const { selectedAddress } = this.props;

    this.watchID = geo.getCurrentPosition(({ coords }) => {
      if (coords) {
        const { latitude, longitude } = coords;
        this.setState(() => ({
          currentLocation: [latitude, longitude],
        }));

        if (!selectedAddress.lon && !selectedAddress.lng) {
          this.panToCoordinates([longitude, latitude]);
        }
      }
    });
  }

  componentWillUnmount() {
    geo.clearWatch(this.watchID);
  }

  onClick = (event) => {
    const feature = event.features[0];
    if (feature) {
      const { properties: { cluster, cluster_id, id }, geometry } = feature;

      if (cluster) { // is cluster click
        const clusterId = cluster_id;
        const mapboxSource = this.mapRef.current.getSource('stop_cluster');
        mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (!err) this.panToCoordinates(geometry.coordinates, zoom);
        });
      } else { // is point click
        const { stops } = this.props;
        const stop = stops.find(s => s.stop_id === id); // get original stop from stop list
        this.setState(() => ({ selectedStop: stop }));
        this.panToCoordinates(geometry.coordinates, this.mapRef.current.getZoom(), 200);
      }
    } else if (this.state.selectedStop) {
      this.setState(() => ({ selectedStop: null }));
    }
  };

  onPopupClose = () => {
    this.setState(() => ({ selectedStop: null }));
  }

  loadImage = (map, image, name) => {
    map.loadImage(image, (error, result) => {
      if (!error) {
        map.addImage(name, result);
      }
    });
  }

  mapOnLoad = () => {
    const map = this.mapRef.current;
    const { selectedAddress, setMap } = this.props;
    setMap(map);
    // eslint-disable-next-line no-unused-expressions
    map.getMap()?.keyboard.disableRotation();

    map.on('mouseenter', 'clusters', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'clusters', () => { map.getCanvas().style.cursor = ''; });
    map.on('mouseenter', 'unclustered-point', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'unclustered-point', () => { map.getCanvas().style.cursor = ''; });

    this.loadImage(map, StopCluster, 'stop-cluster');
    this.loadImage(map, StopBus, 'stop-bus');
    this.loadImage(map, StopFerry, 'stop-ferry');
    this.loadImage(map, StopTrain, 'stop-train');

    // pan to Pin after map load - only for initial Search to open map when the map is not ready
    if (selectedAddress.lat && selectedAddress.lng) {
      this.panToCoordinates([selectedAddress.lng, selectedAddress.lat]);
    }
  }

  panToCoordinates(center, zoom = mapData.zoomLevel.pin, paddingTop = 0) {
    this.mapRef.current && this.mapRef.current.easeTo({
      center,
      duration: panAnimationDuration,
      padding: {
        top: paddingTop,
        bottom: 0,
        left: 0,
        right: 0,
      },
      zoom,
    });
  }

  render() {
    const {
      selectedAddress,
      selectStop,
      stops,
      enableAccessibilityOutlines,
    } = this.props;

    const { currentLocation, selectedStop } = this.state;

    return (
      <section className={['map-container']}>
        <MapGL
          ref={this.mapRef}
          initialViewState={initialViewportState}
          attributionControl={false}
          dragRotate={false}
          interactiveLayerIds={interactiveLayers}
          mapboxApiUrl={process.env.REACT_APP_MAPBOX_SERVER_URL}
          mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
          mapStyle={process.env.REACT_APP_MAPBOX_STYLE_URL}
          maxZoom={maxZoomLevel}
          minZoom={minZoomLevel}
          onLoad={this.mapOnLoad}
          onClick={this.onClick}
        >
          <AttributionControl compact={false} />
          <NavigationControl position="bottom-right" showCompass={false} />
          <PinMarker address={selectedAddress} mapRef={this.mapRef} />
          <StopsLayer stops={stops} selectStop={selectStop} enableAccessibilityOutlines={enableAccessibilityOutlines} selectedStop={selectedStop} onPopupClose={this.onPopupClose} />
          <CurrentLocationMarker position={currentLocation} />
        </MapGL>
      </section>
    );
  }
}

const ContextStopMap = props => (
  <MapWrapperContext.Consumer>
    {({ map, setMap }) => <StopMap {...props} map={map} setMap={setMap} />}
  </MapWrapperContext.Consumer>
);

StopMap.propTypes = {
  enableAccessibilityOutlines: PropTypes.func.isRequired,
  selectedAddress: ShapeOfTypes.Address,
  selectStop: PropTypes.func.isRequired,
  setMap: PropTypes.func.isRequired,
  stops: ShapeOfTypes.Stops,
};

StopMap.defaultProps = {
  selectedAddress: null,
  stops: [],
};

ContextStopMap.propTypes = {
  selectedAddress: ShapeOfTypes.Address,
  selectStop: PropTypes.func.isRequired,
  stops: ShapeOfTypes.Stops,
};

ContextStopMap.defaultProps = {
  selectedAddress: null,
  stops: [],
};

export default ContextStopMap;